import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  Users,
  UserPlus,
  CheckSquare,
  TrendingUp,
  Calendar,
  Mail,
  Phone,
  Globe,
  ArrowRight,
  Plus,
  MessageSquare
} from 'lucide-react';

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalLeads: 0,
    newLeads: 0,
    contactedLeads: 0,
    qualifiedLeads: 0,
    pendingTasks: 0,
    completedTasks: 0,
    totalLeadsChange: 0,
    newLeadsChange: 0,
    conversionRate: 0
  });
  const [recentLeads, setRecentLeads] = useState([]);
  const [upcomingTasks, setUpcomingTasks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [summaryRes, recentLeadsRes, upcomingTasksRes] = await Promise.all([
        axios.get('/dashboard/summary', { headers }),
        axios.get('/dashboard/recent-leads', { headers }),
        axios.get('/dashboard/upcoming-tasks', { headers })
      ]);

      setStats({
        totalLeads: summaryRes.data.totalLeads,
        newLeads: summaryRes.data.newLeads,
        qualifiedLeads: summaryRes.data.qualifiedLeads,
        convertedLeads: summaryRes.data.convertedLeads,
        pendingTasks: summaryRes.data.pendingTasks,
        completedTasks: summaryRes.data.completedTasks,
        totalLeadsChange: summaryRes.data.totalLeadsChange,
        newLeadsChange: summaryRes.data.newLeadsChange,
        conversionRate: summaryRes.data.conversionRate
      });
      setRecentLeads(recentLeadsRes.data);
      setUpcomingTasks(upcomingTasksRes.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      new: 'skylar-badge-new',
      contacted: 'skylar-badge-contacted',
      qualified: 'skylar-badge-qualified',
      disqualified: 'skylar-badge-disqualified'
    };
    return badges[status] || 'skylar-badge-new';
  };

  const getSourceIcon = (source) => {
    const icons = {
      'Website Form': Globe,
      'Email': Mail,
      'Phone Call': Phone,
      'Chat': MessageSquare,
      'Referral': Users,
      'Other': Plus
    };
    return icons[source] || Plus;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-600 mt-1">
            Get insights into your lead management performance
          </p>
        </div>
        <Link
          to="/leads"
          className="skylar-button-primary flex items-center"
        >
          <UserPlus className="w-5 h-5 mr-2" />
          Add Lead
        </Link>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="skylar-card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Leads</p>
              <p className="text-3xl font-bold text-gray-900">{stats.totalLeads}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-lg">
              <Users className="w-6 h-6 text-blue-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <TrendingUp className={`w-4 h-4 mr-1 ${stats.totalLeadsChange >= 0 ? 'text-green-500' : 'text-red-500'}`} />
            <span className={`font-medium ${stats.totalLeadsChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {stats.totalLeadsChange >= 0 ? '+' : ''}{stats.totalLeadsChange}%
            </span>
            <span className="text-gray-500 ml-1">from last month</span>
          </div>
        </div>

        <div className="skylar-card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">New Leads</p>
              <p className="text-3xl font-bold text-gray-900">{stats.newLeads}</p>
            </div>
            <div className="p-3 bg-green-100 rounded-lg">
              <UserPlus className="w-6 h-6 text-green-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <TrendingUp className={`w-4 h-4 mr-1 ${stats.newLeadsChange >= 0 ? 'text-green-500' : 'text-red-500'}`} />
            <span className={`font-medium ${stats.newLeadsChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              {stats.newLeadsChange >= 0 ? '+' : ''}{stats.newLeadsChange}%
            </span>
            <span className="text-gray-500 ml-1">from last week</span>
          </div>
        </div>

        <div className="skylar-card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Qualified Leads</p>
              <p className="text-3xl font-bold text-gray-900">{stats.qualifiedLeads}</p>
            </div>
            <div className="p-3 bg-purple-100 rounded-lg">
              <CheckSquare className="w-6 h-6 text-purple-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
            <span className="text-green-600 font-medium">{stats.conversionRate}%</span>
            <span className="text-gray-500 ml-1">conversion rate</span>
          </div>
        </div>

        <div className="skylar-card p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Pending Tasks</p>
              <p className="text-3xl font-bold text-gray-900">{stats.pendingTasks}</p>
            </div>
            <div className="p-3 bg-orange-100 rounded-lg">
              <Calendar className="w-6 h-6 text-orange-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <span className="text-gray-500">
              {stats.completedTasks} completed this week
            </span>
          </div>
        </div>
      </div>

      {/* Recent Activity Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Recent Leads */}
        <div className="skylar-card">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Recent Leads</h3>
              <Link
                to="/leads"
                className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center"
              >
                View all
                <ArrowRight className="w-4 h-4 ml-1" />
              </Link>
            </div>
          </div>
          <div className="divide-y divide-gray-200">
            {recentLeads.length > 0 ? recentLeads.map((lead) => {
              const SourceIcon = getSourceIcon(lead.source);
              return (
                <div key={lead.id} className="p-6 hover:bg-gray-50 transition-colors duration-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-gray-100 rounded-lg">
                        <SourceIcon className="w-4 h-4 text-gray-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">
                          {lead.name}
                        </p>
                        <p className="text-sm text-gray-600">{lead.company || lead.email}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className={getStatusBadge(lead.status)}>
                        {lead.status}
                      </span>
                      <p className="text-xs text-gray-500 mt-1">
                        {formatDate(lead.created_at)}
                      </p>
                    </div>
                  </div>
                </div>
              );
            }) : (
              <div className="p-6 text-center text-gray-500">
                <Users className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                <p>No leads yet. Start by adding your first lead!</p>
              </div>
            )}
          </div>
        </div>

        {/* Upcoming Tasks */}
        <div className="skylar-card">
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Upcoming Tasks</h3>
              <Link
                to="/tasks"
                className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center"
              >
                View all
                <ArrowRight className="w-4 h-4 ml-1" />
              </Link>
            </div>
          </div>
          <div className="divide-y divide-gray-200">
            {upcomingTasks.length > 0 ? upcomingTasks.map((task) => (
              <div key={task.id} className="p-6 hover:bg-gray-50 transition-colors duration-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <CheckSquare className="w-4 h-4 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{task.title}</p>
                      <p className="text-sm text-gray-600">
                        {task.description || 'No description'}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-900">
                      {formatDate(task.due_date)}
                    </p>
                    <p className="text-xs text-gray-500">Due date</p>
                  </div>
                </div>
              </div>
            )) : (
              <div className="p-6 text-center text-gray-500">
                <CheckSquare className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                <p>No pending tasks. You're all caught up!</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;